import React, { createContext, useContext, ReactNode } from 'react';
import type { OrgType, OrgContextValue } from '../types/context';

const OrgContext = createContext<OrgContextValue | undefined>(undefined);

export const useOrg = (): OrgContextValue => {
  const context = useContext(OrgContext);
  if (!context) {
    throw new Error('useOrg must be used within an OrgProvider');
  }
  return context;
};

interface OrgProviderProps {
  children: ReactNode;
}

/**
 * EPL is the only organization. The former multi-org selector (BARE / EPL /
 * BOTH) has been removed and `org` is permanently 'epl'.
 *
 * The legacy localStorage key 'barebiologics_org' is intentionally retained
 * (pinned to 'epl') rather than renamed: renaming it would invalidate the
 * stored value for every active session, and several components read the key
 * directly. Pinning it at import time also clears any stale 'bare' value left
 * over from a previous session so direct readers can never resolve to 'bare'.
 */
const ORG_VALUE: OrgType = 'epl';

try {
  if (typeof window !== 'undefined' && window.localStorage.getItem('barebiologics_org') !== ORG_VALUE) {
    window.localStorage.setItem('barebiologics_org', ORG_VALUE);
  }
} catch {
  /* localStorage unavailable (SSR / privacy mode) — safe to ignore */
}

export const OrgProvider: React.FC<OrgProviderProps> = ({ children }) => {
  const value: OrgContextValue = {
    org: ORG_VALUE,
    // setOrg / clearOrg are retained as no-ops so the ~30 existing useOrg()
    // call sites keep working without edits. There is no other org to switch to.
    setOrg: () => { /* no-op: EPL is the only organization */ },
    clearOrg: () => { /* no-op: EPL is the only organization */ },
    isHydrating: false,
  };

  return (
    <OrgContext.Provider value={value}>
      {children}
    </OrgContext.Provider>
  );
};

export default OrgContext;
